import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { FiMail, FiSend, FiUserCheck, FiUsers, FiPlus, FiX, FiMessageCircle } from 'react-icons/fi';

export default function ModalEnviarEmailCliente({
  abierto,
  onCerrar,
  cliente,
  asuntoInicial = '',
  cuerpoTextoInicial = '',
  tipoInicial = 'manual',
  documentoId = '',
  documentoTipo = '',
  numeroDocumento = '',
  numeroCustom = '',
  onEnviado,
}) {
  const { empresa } = useAuth();
  const [para, setPara] = useState('');
  const [ccSeleccionados, setCcSeleccionados] = useState([]);
  const [ccCustomInput, setCcCustomInput] = useState('');
  const [asunto, setAsunto] = useState('');
  const [cuerpoTexto, setCuerpoTexto] = useState('');
  const [tipo, setTipo] = useState('manual');
  const [adjuntos, setAdjuntos] = useState([]);
  const [equipo, setEquipo] = useState([]);
  const [destinoWhatsapp, setDestinoWhatsapp] = useState('cliente');
  const [whatsappContactoId, setWhatsappContactoId] = useState('');
  const [whatsappManual, setWhatsappManual] = useState('');
  const [paso, setPaso] = useState(1);
  const [canalSeleccionado, setCanalSeleccionado] = useState('email');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(null);

  useEffect(() => {
    if (abierto && cliente) {
      setPara(cliente.email || '');
      setCcSeleccionados([]);
      setCcCustomInput('');
      setAsunto(asuntoInicial || '');
      setCuerpoTexto(cuerpoTextoInicial || '');
      setTipo(tipoInicial || 'manual');
      setAdjuntos([]);

      const telefonoCliente = cliente?.telefono || cliente?.movil || cliente?.telefonoMovil || '';
      const primerContactoConTelefono = Array.isArray(cliente.personasContacto)
        ? cliente.personasContacto.find((pc) => pc.telefono && pc.telefono.trim())
        : null;
      const contactoIdDefault = primerContactoConTelefono
        ? (primerContactoConTelefono._id || `${primerContactoConTelefono.nombre}-${primerContactoConTelefono.telefono}`)
        : '';

      setDestinoWhatsapp(telefonoCliente ? 'cliente' : (contactoIdDefault ? 'contacto' : 'manual'));
      setWhatsappContactoId(contactoIdDefault);
      setWhatsappManual('');
      setPaso(1);
      setCanalSeleccionado('email');
      setError(null);
      setExito(null);

      api.get('/usuarios')
        .then(({ data }) => setEquipo(Array.isArray(data) ? data.filter((u) => u.rol !== 'superadmin' && u.email) : []))
        .catch(() => setEquipo([]));
    }
  }, [abierto, cliente]);

  if (!cliente) return null;

  const toggleCC = (email) => {
    if (!email) return;
    const mail = String(email).trim().toLowerCase();
    setCcSeleccionados((prev) =>
      prev.includes(mail) ? prev.filter((e) => e !== mail) : [...prev, mail]
    );
  };

  const anadirCcCustom = (e) => {
    e?.preventDefault();
    const mail = String(ccCustomInput).trim().toLowerCase();
    if (!mail) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mail)) {
      setError('El email CC adicional no tiene un formato válido.');
      return;
    }
    setError(null);
    if (!ccSeleccionados.includes(mail)) {
      setCcSeleccionados((prev) => [...prev, mail]);
    }
    setCcCustomInput('');
  };

  const removerCC = (email) => {
    setCcSeleccionados((prev) => prev.filter((e) => e !== email));
  };

  const manejarAdjuntos = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) {
      setAdjuntos([]);
      return;
    }

    const maxPermitidos = documentoId ? 4 : 5;
    const invalid = files.find((file) => file.size > 5 * 1024 * 1024);
    if (invalid) {
      setError('Cada archivo adjunto no puede superar 5 MB.');
      event.target.value = '';
      return;
    }

    if (files.length > maxPermitidos) {
      setError(documentoId
        ? 'Con el PDF del documento adjunto, puedes incluir hasta 4 archivos extra.'
        : 'Solo puedes adjuntar un máximo de 5 archivos por email.');
      event.target.value = '';
      return;
    }

    setError(null);
    setAdjuntos(files.slice(0, maxPermitidos));
  };

  const prepararTextoWhatsApp = (urlFirma = '') => {
    const nombreCliente = cliente?.nombre || 'cliente';
    const nombreEmpresa = empresa?.nombre || 'mi empresa';

    if (!documentoId || !documentoTipo) {
      const partes = [
        `Buenos días ${nombreCliente},`,
        '',
        `Le escribo desde la Empresa ${nombreEmpresa} en relación a ...`,
        '',
        'Quedo atento/a a su respuesta.',
      ];
      return partes.join('\n');
    }

    const tipoDocumento = documentoTipo === 'factura' ? 'la factura' : 'el presupuesto';
    const numeroDoc = String(numeroDocumento || '').trim();

    const partes = [
      `Hola ${nombreCliente},`,
      '',
      `Te enviamos ${tipoDocumento}${numeroDoc ? ` ${numeroDoc}` : ''}.`,
      urlFirma ? `Puedes revisarlo y firmarlo aquí: ${urlFirma}` : 'Si quieres, lo revisamos juntos por aquí y cerramos lo que haga falta.',
      'Gracias.',
    ];

    return partes.join('\n');
  };

  const normalizarNumeroWhatsApp = (valor) => {
    if (!valor) return '';

    const digits = String(valor).replace(/\D/g, '');
    if (!digits) return '';

    let numero = digits.replace(/^00/, '');

    if (numero.startsWith('34') && numero.length === 11) {
      return `+${numero}`;
    }

    if (numero.length === 9) {
      return `+34${numero}`;
    }

    if (numero.length > 9 && numero.startsWith('34')) {
      return `+${numero}`;
    }

    return `+${numero}`;
  };

  const obtenerTelefonoParaWhatsApp = () => {
    if (destinoWhatsapp === 'cliente') {
      return normalizarNumeroWhatsApp(cliente?.telefono || cliente?.movil || cliente?.telefonoMovil || '');
    }

    if (destinoWhatsapp === 'contacto') {
      const contacto = Array.isArray(cliente?.personasContacto)
        ? cliente.personasContacto.find((pc) => String(pc?._id || '').trim() === String(whatsappContactoId || '').trim())
        : null;
      if (!contacto) return '';
      return normalizarNumeroWhatsApp(contacto.telefono || '');
    }

    if (destinoWhatsapp === 'manual') {
      return normalizarNumeroWhatsApp(whatsappManual || '');
    }

    return '';
  };

  const enviarWhatsApp = async () => {
    try {
      let urlFirma = '';

      if (documentoId && ['presupuesto', 'factura'].includes(documentoTipo)) {
        const endpoint = documentoTipo === 'factura'
          ? `/facturas/${documentoId}/generar-link-firma`
          : `/presupuestos/${documentoId}/generar-link-firma`;

        const { data } = await api.post(endpoint, { confirmarInvalidacion: true });
        urlFirma = data?.urlFirma || '';
      }

      const texto = prepararTextoWhatsApp(urlFirma);
      if (!texto) {
        setError('No se pudo preparar el texto para WhatsApp.');
        return;
      }

      const numero = obtenerTelefonoParaWhatsApp();
      if (!numero) {
        setError('Debes elegir un teléfono válido del cliente, de un contacto o introducirlo manualmente antes de abrir WhatsApp.');
        return;
      }

      const numeroParaWhatsApp = numero.replace(/^\+/, '');
      const url = `https://api.whatsapp.com/send?phone=${encodeURIComponent(numeroParaWhatsApp)}&text=${encodeURIComponent(texto)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
      setExito('Se ha abierto WhatsApp con el enlace del documento actualizado.');
      setTimeout(() => {
        setExito(null);
        onCerrar();
      }, 1600);
    } catch (err) {
      console.error('ERROR enviarWhatsApp ->', err?.response?.data || err);
      setError(err.response?.data?.error || 'No se pudo generar el enlace del documento para WhatsApp.');
    }
  };

  const enviar = async (e) => {
    e.preventDefault();
    if (!para) {
      setError('El destinatario principal (email del cliente) es obligatorio.');
      return;
    }
    if (!asunto.trim()) {
      setError('El asunto del correo es obligatorio.');
      return;
    }
    if (!cuerpoTexto.trim()) {
      setError('El mensaje del correo es obligatorio.');
      return;
    }

    setEnviando(true);
    setError(null);
    setExito(null);

    const ccFinal = ccSeleccionados.join(', ');

    try {
      const formData = new FormData();
      formData.append('clienteId', cliente._id || cliente.id);
      formData.append('para', para.trim());
      formData.append('cc', ccFinal);
      formData.append('asunto', asunto.trim());
      formData.append('cuerpoTexto', cuerpoTexto.trim());
      formData.append('tipo', tipo || 'manual');
      if (documentoId) formData.append('documentoId', String(documentoId));
      if (documentoTipo) formData.append('documentoTipo', String(documentoTipo));
      if (adjuntos.length) {
        adjuntos.forEach((archivo) => formData.append('adjuntos', archivo));
      }

      await api.post('/email-logs/enviar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setExito('Correo electrónico enviado correctamente.');
      if (typeof onEnviado === 'function') {
        onEnviado();
      }
      setTimeout(() => {
        setExito(null);
        onCerrar();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al enviar el correo electrónico.');
    } finally {
      setEnviando(false);
    }
  };

  const personasContacto = Array.isArray(cliente.personasContacto)
    ? cliente.personasContacto.filter((pc) => pc.email && pc.email.trim())
    : [];
  const personasContactoConTelefono = Array.isArray(cliente.personasContacto)
    ? cliente.personasContacto.filter((pc) => pc.telefono && pc.telefono.trim())
    : [];
  const usaNumeroAutogenerado = !String(numeroCustom || '').trim();

  const puedeContinuarPaso = () => {
    if (paso === 1) return Boolean(canalSeleccionado);
    if (canalSeleccionado === 'email') return Boolean(para || cliente?.email);
    return true;
  };

  const continuarWizard = () => {
    if (paso === 1) {
      setPaso(2);
      setError(null);
      return;
    }
    if (canalSeleccionado === 'email') {
      const form = document.getElementById('form-envio-documento');
      if (form) {
        form.requestSubmit();
      }
      return;
    }
    enviarWhatsApp();
  };

  return (
    <Modal abierto={abierto} onCerrar={onCerrar} titulo={`Enviar ${documentoTipo === 'factura' ? 'factura' : 'presupuesto'} a ${cliente.nombre}`} ancho={650}>
      <div className="form-grid" style={{ gap: 14 }}>
        {error && <div className="alert-error span-2">{error}</div>}
        {exito && <div className="alert-ok span-2">{exito}</div>}
        {documentoTipo && usaNumeroAutogenerado && (
          <div className="alert-warning span-2">
            Se va a usar el código autogenerado no consecutivo para este {documentoTipo === 'factura' ? 'factura' : 'presupuesto'}: {String(numeroDocumento || 'Número automático').trim() || 'Número automático'}.
          </div>
        )}

        <div className="span-2" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
          <span className="badge badge-info" style={{ opacity: paso === 1 ? 1 : 0.7 }}>1. Canal</span>
          <span style={{ color: 'var(--text-soft)' }}>→</span>
          <span className="badge badge-info" style={{ opacity: paso === 2 ? 1 : 0.7 }}>2. Datos</span>
        </div>

        {paso === 1 ? (
          <>
            <div className="span-2" style={{ display: 'grid', gap: 12 }}>
              <button
                type="button"
                className={`btn ${canalSeleccionado === 'email' ? 'btn-primary' : ''}`}
                onClick={() => setCanalSeleccionado('email')}
                style={{
                  justifyContent: 'flex-start',
                  padding: '22px 18px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  border: canalSeleccionado === 'email' ? '2px solid var(--primary)' : '1px solid var(--border)',
                  background: canalSeleccionado === 'email' ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#fff',
                  color: canalSeleccionado === 'email' ? '#fff' : '#0f172a',
                  boxShadow: canalSeleccionado === 'email' ? '0 8px 18px rgba(37, 99, 235, 0.25)' : 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}><FiMail size={18} /> Enviar por email</span>
              </button>

              <button
                type="button"
                className={`btn ${canalSeleccionado === 'whatsapp' ? 'btn-primary' : ''}`}
                onClick={() => setCanalSeleccionado('whatsapp')}
                style={{
                  justifyContent: 'flex-start',
                  padding: '22px 18px',
                  fontSize: '1rem',
                  fontWeight: 700,
                  border: canalSeleccionado === 'whatsapp' ? '2px solid #16a34a' : '1px solid var(--border)',
                  background: canalSeleccionado === 'whatsapp' ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' : '#fff',
                  color: canalSeleccionado === 'whatsapp' ? '#fff' : '#0f172a',
                  boxShadow: canalSeleccionado === 'whatsapp' ? '0 8px 18px rgba(22, 163, 74, 0.25)' : 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}><FiMessageCircle size={18} /> Enviar por WhatsApp</span>
              </button>
            </div>
          </>
        ) : null}

        {paso === 2 && canalSeleccionado === 'email' ? (
          <form id="form-envio-documento" onSubmit={enviar} className="form-grid span-2" style={{ gap: 14 }}>
            <div className="span-2" style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, marginBottom: 4 }}>
              <FiMail /> Enviar por email
            </div>

            <label className="field span-2">
              <span>Destinatario principal</span>
              <input
                type="email"
                value={para}
                readOnly
                required
                style={{ background: 'var(--bg-card, #f8fafc)', fontWeight: 600 }}
              />
            </label>

            <div className="span-2" style={{ background: 'var(--bg-card, #f8fafc)', border: '1px solid var(--border, #e2e8f0)', padding: 14, borderRadius: 10 }}>
              <span style={{ fontWeight: 600, fontSize: '0.88rem', display: 'block', marginBottom: 8 }}>
                Añadir en Copia (CC)
              </span>

              {personasContacto.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-soft)', marginBottom: 4 }}>
                    <FiUsers style={{ verticalAlign: '-1px', marginRight: 3 }} /> Personas de contacto del cliente:
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {personasContacto.map((pc) => {
                      const mail = pc.email.toLowerCase().trim();
                      const marcado = ccSeleccionados.includes(mail);
                      return (
                        <button
                          key={pc._id || mail}
                          type="button"
                          className={`btn mini ${marcado ? 'btn-primary' : ''}`}
                          onClick={() => toggleCC(mail)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          {marcado ? '✓ ' : '+ '}{pc.nombre} {pc.apellidos} ({mail})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {equipo.length > 0 && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-soft)', marginBottom: 4 }}>
                    <FiUserCheck style={{ verticalAlign: '-1px', marginRight: 3 }} /> Equipo de la empresa:
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {equipo.map((u) => {
                      const mail = u.email.toLowerCase().trim();
                      const marcado = ccSeleccionados.includes(mail);
                      return (
                        <button
                          key={u._id}
                          type="button"
                          className={`btn mini ${marcado ? 'btn-primary' : ''}`}
                          onClick={() => toggleCC(mail)}
                          style={{ fontSize: '0.8rem' }}
                        >
                          {marcado ? '✓ ' : '+ '}{u.nombre} {u.apellidos || ''} ({mail})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
                <input
                  type="email"
                  placeholder="Otro email CC..."
                  value={ccCustomInput}
                  onChange={(e) => setCcCustomInput(e.target.value)}
                  style={{ flex: 1, fontSize: '0.85rem' }}
                />
                <button type="button" className="btn mini" onClick={anadirCcCustom}>
                  <FiPlus /> Añadir CC
                </button>
              </div>

              {ccSeleccionados.length > 0 && (
                <div style={{ marginTop: 10, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>CC activos:</span>
                  {ccSeleccionados.map((mail) => (
                    <span
                      key={mail}
                      className="badge badge-info"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', fontSize: '0.8rem' }}
                    >
                      {mail}
                      <FiX style={{ cursor: 'pointer' }} onClick={() => removerCC(mail)} />
                    </span>
                  ))}
                </div>
              )}
            </div>

            <label className="field span-2">
              <span>Asunto *</span>
              <input
                type="text"
                required
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
                placeholder="Ej: Seguimiento de propuesta comercial"
              />
            </label>

            <label className="field span-2"><span>Mensaje / Cuerpo del correo *</span>
              <textarea
                rows={7}
                required
                value={cuerpoTexto}
                onChange={(e) => setCuerpoTexto(e.target.value)}
                placeholder="Escribe el mensaje completo..."
                style={{ resize: 'vertical' }}
              />
            </label>

            <label className="field span-2">
              <span>Adjuntos ({adjuntos.length}/{documentoId ? 4 : 5}, máx. 5 MB cada uno)</span>
              <input type="file" multiple accept="*/*" onChange={manejarAdjuntos} />
              {adjuntos.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {adjuntos.map((archivo, idx) => (
                    <span key={`${archivo.name}-${idx}`} className="badge badge-info">{archivo.name}</span>
                  ))}
                </div>
              )}
            </label>
          </form>
        ) : null}

        {paso === 2 && canalSeleccionado === 'whatsapp' ? (
          <div className="span-2" style={{ background: 'var(--bg-card, #f8fafc)', border: '1px solid var(--border, #e2e8f0)', padding: 16, borderRadius: 12 }}>
            <div style={{ fontWeight: 800, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, fontSize: '1rem' }}>
              <FiMessageCircle /> Enviar por WhatsApp
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-soft)', marginBottom: 12 }}>
              Elige el teléfono del destinatario. El enlace del documento se genera automáticamente.
            </div>

            <div style={{ display: 'grid', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', border: '1px solid var(--border, #e2e8f0)', borderRadius: 10, background: destinoWhatsapp === 'cliente' ? 'rgba(34,197,94,0.07)' : '#fff' }}>
                <input
                  type="radio"
                  name="destinoWhatsapp"
                  checked={destinoWhatsapp === 'cliente'}
                  onChange={() => setDestinoWhatsapp('cliente')}
                />
                <span>
                  <strong>Teléfono del cliente</strong>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)' }}>
                    {cliente?.telefono || cliente?.movil || cliente?.telefonoMovil || 'No hay teléfono principal registrado'}
                  </div>
                </span>
              </label>

              {personasContactoConTelefono.length > 0 && (
                <div style={{ display: 'grid', gap: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '10px 12px', border: '1px solid var(--border, #e2e8f0)', borderRadius: 10, background: destinoWhatsapp === 'contacto' ? 'rgba(59,130,246,0.07)' : '#fff' }}>
                    <input
                      type="radio"
                      name="destinoWhatsapp"
                      checked={destinoWhatsapp === 'contacto'}
                      onChange={() => setDestinoWhatsapp('contacto')}
                    />
                    <span style={{ flex: 1 }}>
                      <strong>Teléfono de un contacto</strong>
                    </span>
                  </label>
                  <select
                    value={whatsappContactoId}
                    onChange={(e) => {
                      setWhatsappContactoId(e.target.value);
                      setDestinoWhatsapp('contacto');
                    }}
                    style={{ maxWidth: 420, width: '100%' }}
                  >
                    <option value="">Selecciona el contacto que quieres usar</option>
                    {personasContactoConTelefono.map((pc) => (
                      <option key={pc._id || `${pc.nombre}-${pc.telefono}`} value={pc._id || `${pc.nombre}-${pc.telefono}`}>
                        {pc.nombre} {pc.apellidos} — {pc.telefono}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <label style={{ display: 'grid', gap: 8, cursor: 'pointer', padding: '10px 12px', border: '1px solid var(--border, #e2e8f0)', borderRadius: 10, background: destinoWhatsapp === 'manual' ? 'rgba(245,158,11,0.07)' : '#fff' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="radio"
                    name="destinoWhatsapp"
                    checked={destinoWhatsapp === 'manual'}
                    onChange={() => setDestinoWhatsapp('manual')}
                  />
                  <strong>Escribir número manualmente</strong>
                </span>
                <input
                  type="tel"
                  value={whatsappManual}
                  onChange={(e) => {
                    setWhatsappManual(e.target.value);
                    setDestinoWhatsapp('manual');
                  }}
                  placeholder="Ej: +34 600 000 000"
                  style={{ maxWidth: 420, width: '100%' }}
                />
              </label>
            </div>
          </div>
        ) : null}

        <div className="form-actions span-2" style={{ justifyContent: 'space-between', marginTop: 8 }}>
          <button type="button" className="btn" onClick={onCerrar}>
            Cancelar
          </button>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {paso === 2 ? (
              <button type="button" className="btn" onClick={() => setPaso(1)}>
                Atrás
              </button>
            ) : null}

            {paso === 1 ? (
              <button type="button" className="btn btn-primary" onClick={continuarWizard} disabled={!puedeContinuarPaso()}>
                Continuar
              </button>
            ) : null}

            {paso === 2 && canalSeleccionado === 'email' ? (
              <button type="button" className="btn btn-primary" onClick={continuarWizard} disabled={!puedeContinuarPaso()}>
                <FiSend /> Enviar correo electrónico
              </button>
            ) : null}

            {paso === 2 && canalSeleccionado === 'whatsapp' ? (
              <button type="button" className="btn btn-primary" onClick={enviarWhatsApp}>
                <FiMessageCircle /> Enviar por WhatsApp
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
}